/**
 * BootcampWeekDetail — modal showing one week's full content and the
 * student's progress UI (prep button, deliverable submission form, quiz
 * link, facilitator notes).
 */

import { useCallback, useEffect, useState } from 'react';
import { Check, CheckCircle2, Clock, FileText, PlayCircle } from 'lucide-react';
import Modal from '../ui/Modal';
import Button from '../ui/Button';
import { Skeleton } from '../Skeleton';
import { useToastContext } from '../ToastProvider';
import {
    getBootcampWeekDetail,
    markPrepComplete,
    submitWeekDeliverables,
    type BootcampWeekDetail as WeekDetailType,
    type DeliverableSchemaItem,
} from '../../bootcampService';
import BootcampQuizFlow from './BootcampQuizFlow';
import QuizAttemptHistory from './QuizAttemptHistory';
import './BootcampWeekDetail.css';

type Props = {
    weekNumber: number;
    onClose: () => void;
    onChanged: () => void;
};

function BootcampWeekDetail({ weekNumber, onClose, onChanged }: Props) {
    const [week, setWeek] = useState<WeekDetailType | null>(null);
    const [loading, setLoading] = useState(true);
    const [prepBusy, setPrepBusy] = useState(false);
    const [submitBusy, setSubmitBusy] = useState(false);
    const [deliverables, setDeliverables] = useState<Record<string, string>>({});
    const [activeQuizId, setActiveQuizId] = useState<string | null>(null);
    // Bumped when the quiz modal closes after a new attempt so the
    // attempt-history list refetches without remounting.
    const [quizHistoryKey, setQuizHistoryKey] = useState(0);
    const { success, error: toastError } = useToastContext();

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const data = await getBootcampWeekDetail(weekNumber);
            setWeek(data);
            // Prefill deliverables from server state
            const initial: Record<string, string> = {};
            const existing = (data.progress?.deliverables ?? {}) as Record<string, unknown>;
            for (const item of data.deliverables_schema) {
                const value = existing[item.key];
                initial[item.key] = typeof value === 'string' ? value : '';
            }
            setDeliverables(initial);
        } catch (err) {
            console.error('[BootcampWeekDetail] load failed', err);
            toastError("Couldn't load this week.");
        } finally {
            setLoading(false);
        }
    }, [weekNumber, toastError]);

    useEffect(() => {
        void load();
    }, [load]);

    const handlePrep = useCallback(async () => {
        if (!week || prepBusy) return;
        setPrepBusy(true);
        try {
            await markPrepComplete(weekNumber);
            success('Prep marked complete.');
            await load();
            onChanged();
        } catch (err) {
            console.error('[BootcampWeekDetail] prep failed', err);
            toastError("Couldn't mark prep complete.");
        } finally {
            setPrepBusy(false);
        }
    }, [load, onChanged, prepBusy, success, toastError, week, weekNumber]);

    const handleSubmit = useCallback(async () => {
        if (!week || submitBusy) return;
        setSubmitBusy(true);
        try {
            await submitWeekDeliverables(weekNumber, deliverables);
            success('Deliverables submitted. A facilitator will review them.');
            await load();
            onChanged();
        } catch (err) {
            console.error('[BootcampWeekDetail] deliverables submit failed', err);
            const msg = err instanceof Error ? err.message : 'Submission failed.';
            toastError(msg);
        } finally {
            setSubmitBusy(false);
        }
    }, [deliverables, load, onChanged, submitBusy, success, toastError, week, weekNumber]);

    const prepComplete = week?.progress?.prep_completed ?? false;
    const alreadySubmitted = week?.progress?.deliverables_submitted ?? false;
    const passStatus = week?.progress?.pass_status ?? 'pending';
    const canEdit = passStatus === 'pending' || passStatus === 'retry';

    return (
        <Modal
            open
            onClose={onClose}
            size="lg"
            ariaLabel={week ? `Week ${week.week_number} — ${week.course_title}` : 'Week details'}
        >
            <Modal.Header onClose={onClose}>
                {week ? `Week ${week.week_number} — ${week.course_title}` : 'Loading week…'}
            </Modal.Header>
            <Modal.Body>
                {loading ? (
                    <>
                        <Skeleton width="100%" height="20px" borderRadius="6px" />
                        <Skeleton width="80%" height="20px" borderRadius="6px" />
                        <Skeleton width="100%" height="80px" borderRadius="8px" />
                        <Skeleton width="100%" height="80px" borderRadius="8px" />
                    </>
                ) : !week ? (
                    <p>Week not available.</p>
                ) : (
                    <>
                        {week.theme && (
                            <p className="bcwd__theme">“{week.theme}”</p>
                        )}
                        {week.course_description && (
                            <p className="bcwd__description">{week.course_description}</p>
                        )}

                        {/* ── Lessons list ─────────────────────────────── */}
                        <section className="bcwd__section">
                            <h3>
                                <FileText size={16} aria-hidden="true" /> Lessons ({week.lessons.length})
                            </h3>
                            <ul className="bcwd__lessons">
                                {week.lessons.map((lesson) => (
                                    <li key={lesson.id} className="bcwd__lesson">
                                        <span className="bcwd__lesson-title">{lesson.title}</span>
                                        <span className="bcwd__lesson-duration">
                                            <Clock size={12} aria-hidden="true" /> {lesson.duration}
                                        </span>
                                    </li>
                                ))}
                            </ul>
                        </section>

                        {/* ── Quizzes ──────────────────────────────────── */}
                        {week.quizzes.length > 0 && (
                            <section className="bcwd__section">
                                <h3>
                                    <PlayCircle size={16} aria-hidden="true" /> Pre-assessment quiz
                                </h3>
                                <ul className="bcwd__quizzes">
                                    {week.quizzes.map((quiz) => (
                                        <li key={quiz.id} className="bcwd__quiz">
                                            <div className="bcwd__quiz-head">
                                                <strong>{quiz.title}</strong>
                                                <span className="bcwd__quiz-meta">
                                                    {quiz.question_count} questions · {quiz.time_limit} min ·
                                                    pass {quiz.passing_score}%
                                                </span>
                                            </div>
                                            <p className="bcwd__quiz-desc">{quiz.description}</p>
                                            <QuizAttemptHistory
                                                quizId={quiz.id}
                                                refreshKey={quizHistoryKey}
                                            />
                                            <Button
                                                variant="primary"
                                                size="md"
                                                icon={<PlayCircle size={16} />}
                                                onClick={() => setActiveQuizId(quiz.id)}
                                            >
                                                Start assessment
                                            </Button>
                                        </li>
                                    ))}
                                </ul>
                            </section>
                        )}

                        {/* ── Prep button ──────────────────────────────── */}
                        <section className="bcwd__section">
                            <h3>
                                <Check size={16} aria-hidden="true" /> Pre-session preparation
                            </h3>
                            {prepComplete ? (
                                <p className="bcwd__prep-complete">
                                    <CheckCircle2 size={16} aria-hidden="true" /> Prep complete. Nice work.
                                </p>
                            ) : (
                                <Button
                                    variant="secondary"
                                    size="md"
                                    icon={<Check size={16} />}
                                    onClick={handlePrep}
                                    disabled={prepBusy}
                                >
                                    {prepBusy ? 'Saving…' : 'Mark prep complete'}
                                </Button>
                            )}
                        </section>

                        {/* ── Deliverables ─────────────────────────────── */}
                        <section className="bcwd__section">
                            <h3>
                                <FileText size={16} aria-hidden="true" /> Weekly deliverables
                            </h3>
                            {passStatus === 'pass' ? (
                                <div className="bcwd__status bcwd__status--pass">
                                    <CheckCircle2 size={16} aria-hidden="true" />
                                    <div>
                                        <strong>Passed</strong>
                                        {week.progress?.reviewer_notes && (
                                            <p>{week.progress.reviewer_notes}</p>
                                        )}
                                    </div>
                                </div>
                            ) : passStatus === 'submitted' ? (
                                <div className="bcwd__status bcwd__status--submitted">
                                    <Clock size={16} aria-hidden="true" />
                                    <div>
                                        <strong>Awaiting facilitator review</strong>
                                        <p>You'll be notified when the review is complete.</p>
                                    </div>
                                </div>
                            ) : (
                                <>
                                    {passStatus === 'retry' && week.progress?.reviewer_notes && (
                                        <div className="bcwd__status bcwd__status--retry">
                                            <strong>Retry requested</strong>
                                            <p>{week.progress.reviewer_notes}</p>
                                        </div>
                                    )}
                                    <DeliverableFields
                                        schema={week.deliverables_schema}
                                        values={deliverables}
                                        onChange={setDeliverables}
                                        disabled={!canEdit || submitBusy}
                                    />
                                    <Button
                                        variant="primary"
                                        size="md"
                                        onClick={handleSubmit}
                                        disabled={!canEdit || submitBusy}
                                    >
                                        {submitBusy
                                            ? 'Submitting…'
                                            : alreadySubmitted
                                                ? 'Resubmit deliverables'
                                                : 'Submit deliverables'}
                                    </Button>
                                </>
                            )}
                        </section>
                    </>
                )}
            </Modal.Body>
            <Modal.Footer>
                <Button variant="ghost" size="md" onClick={onClose}>
                    Close
                </Button>
            </Modal.Footer>

            {/* Quiz modal mounts on top of this modal */}
            {activeQuizId && (
                <BootcampQuizFlow
                    quizId={activeQuizId}
                    onClose={() => {
                        setActiveQuizId(null);
                        // Force QuizAttemptHistory to refetch — the
                        // user just created a new attempt.
                        setQuizHistoryKey((k) => k + 1);
                    }}
                />
            )}
        </Modal>
    );
}

// ── Deliverable field renderer ──────────────────────────────────────────

function DeliverableFields({
    schema,
    values,
    onChange,
    disabled,
}: {
    schema: DeliverableSchemaItem[];
    values: Record<string, string>;
    onChange: (next: Record<string, string>) => void;
    disabled: boolean;
}) {
    return (
        <div className="bcwd__deliverables">
            {schema.map((item) => (
                <label key={item.key} className="bcwd__deliverable-field">
                    <span className="bcwd__deliverable-label">
                        {item.label}
                        {item.required !== false && (
                            <span className="bcwd__required" aria-hidden="true"> *</span>
                        )}
                    </span>
                    {item.description && (
                        <span className="bcwd__deliverable-hint">{item.description}</span>
                    )}
                    <textarea
                        rows={4}
                        value={values[item.key] ?? ''}
                        onChange={(e) =>
                            onChange({ ...values, [item.key]: e.target.value })
                        }
                        disabled={disabled}
                        placeholder={item.type === 'url' ? 'https://…' : ''}
                    />
                </label>
            ))}
        </div>
    );
}

export default BootcampWeekDetail;
